import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { fileUrl, formatRupiah, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brand } from "@/components/Brand";
import { Building2, MapPin, Phone, MessageSquare, ArrowLeft, Bed, Bath, Expand, Home } from "lucide-react";

const STATUS_LABELS = { baru: "Baru", on_market: "On Market", nego: "Nego", closing: "Closing", sold: "SOLD", reserved: "Reserved" };

export function PublicListingPage() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/listings/${id}`, { withCredentials: true });
        setL(data);
      } catch (e) { setErr("Listing tidak tersedia"); }
    })();
  }, [id]);

  const trackWa = () => {
    axios.post(`${API}/public/listings/${id}/track?event=whatsapp`).catch(() => {});
  };

  if (err) return <div className="min-h-screen grid place-items-center text-slate-400 bg-[#0A0D14]">{err}</div>;
  if (!l) return <div className="min-h-screen grid place-items-center text-slate-400 bg-[#0A0D14]">Memuat…</div>;

  const waLink = `https://wa.me/${(l.agent_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Halo ${l.agent_name}, saya tertarik dengan listing: ${l.title}`)}`;

  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-100">
      <header className="border-b border-white/5 bg-[#0A0D14]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
            <Brand />
          </Link>
          <Link to={`/catalog/${l.agent_id}`} className="text-sm text-slate-400 hover:text-orange-400">Lihat katalog agent →</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="public-listing-page">
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-3">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#0D1322] border border-white/5 relative">
              {l.photos?.length ? (
                <img src={fileUrl(l.photos[activePhoto])} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-slate-600"><Building2 className="w-16 h-16" /></div>
              )}
              <div className="absolute top-4 left-4">
                <Badge className="bg-black/60 border-orange-500/30 text-orange-300 uppercase text-[10px] tracking-widest">{STATUS_LABELS[l.status]}</Badge>
              </div>
            </div>
            {l.photos?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {l.photos.map((p, i) => (
                  <button key={p} onClick={() => setActivePhoto(i)} className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 ${i === activePhoto ? "border-orange-500" : "border-white/5"}`}>
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl bg-[#141C2E] border border-white/5 p-6">
              <div className="text-[11px] uppercase tracking-widest text-orange-400 font-semibold">{l.property_type}</div>
              <h1 className="text-2xl font-extrabold tracking-tight mt-1">{l.title}</h1>
              <div className="text-slate-400 text-sm mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.location}</div>
              <div className="text-3xl font-extrabold text-orange-400 mt-4">{formatRupiah(l.price)}</div>

              <div className="mt-5 grid grid-cols-4 gap-2 text-xs">
                <Spec icon={<Expand className="w-3.5 h-3.5" />} label="LT" value={l.land_area ? `${l.land_area} m²` : "—"} />
                <Spec icon={<Home className="w-3.5 h-3.5" />} label="LB" value={l.building_area ? `${l.building_area} m²` : "—"} />
                <Spec icon={<Bed className="w-3.5 h-3.5" />} label="KT" value={l.bedrooms ?? "—"} />
                <Spec icon={<Bath className="w-3.5 h-3.5" />} label="KM" value={l.bathrooms ?? "—"} />
              </div>

              <div className="mt-5 border-t border-white/5 pt-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Agent</div>
                <div className="font-semibold mt-1">{l.agent_name}</div>
                {l.agent_agency && <div className="text-xs text-slate-400">{l.agent_agency}</div>}
                {l.agent_phone && <div className="text-xs text-slate-500 mt-0.5">{l.agent_phone}</div>}
              </div>

              <a href={waLink} target="_blank" rel="noreferrer" onClick={trackWa} data-testid="public-wa-btn">
                <Button className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 font-semibold py-6">
                  <MessageSquare className="w-4 h-4 mr-1" /> Chat Agent via WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>

        {l.description && (
          <div className="rounded-2xl bg-[#141C2E] border border-white/5 p-6">
            <h3 className="font-semibold mb-3">Deskripsi</h3>
            <p className="text-sm text-slate-300 whitespace-pre-line leading-relaxed">{l.description}</p>
          </div>
        )}
        {l.facilities?.length > 0 && (
          <div className="rounded-2xl bg-[#141C2E] border border-white/5 p-6">
            <h3 className="font-semibold mb-3">Fasilitas</h3>
            <div className="flex flex-wrap gap-2">
              {l.facilities.map((f) => (
                <span key={f} className="px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs font-medium">{f}</span>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="p-2 rounded-lg bg-[#0D1322] border border-white/5">
      <div className="flex items-center gap-1 text-slate-500 text-[10px] uppercase tracking-widest">{icon}{label}</div>
      <div className="font-semibold text-slate-100 mt-0.5 text-sm">{value}</div>
    </div>
  );
}

export function PublicCatalogPage() {
  const { agentId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/catalog/${agentId}`);
        setData(data);
      } catch { setErr("Katalog tidak tersedia"); }
    })();
  }, [agentId]);

  if (err) return <div className="min-h-screen grid place-items-center text-slate-400 bg-[#0A0D14]">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-400 bg-[#0A0D14]">Memuat…</div>;

  const waLink = data.agent.phone
    ? `https://wa.me/${(data.agent.phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Halo ${data.agent.name}, saya tertarik dengan properti Anda.`)}`
    : null;

  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-100">
      <header className="border-b border-white/5 bg-[#0A0D14]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="catalog-logo">
            <Brand />
          </Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-10 space-y-8" data-testid="public-catalog-page">
        <section className="rounded-2xl bg-gradient-to-br from-[#141C2E] to-[#0D1322] border border-white/5 p-8 flex flex-col sm:flex-row items-start gap-6">
          <div className="w-20 h-20 rounded-full bg-orange-500/20 border border-orange-500/30 grid place-items-center text-2xl font-extrabold text-orange-300">
            {(data.agent.name || "A").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-orange-400 font-semibold">Katalog Agent ListingPro</div>
            <h1 className="text-3xl font-extrabold mt-1">{data.agent.name}</h1>
            {data.agent.agency_name && <div className="text-slate-400">{data.agent.agency_name}</div>}
            {data.agent.phone && <div className="text-slate-500 text-sm mt-1 flex items-center gap-1"><Phone className="w-3 h-3" /> {data.agent.phone}</div>}
            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" className="inline-block mt-4" data-testid="catalog-wa-btn">
                <Button className="bg-emerald-600 hover:bg-emerald-700 font-semibold">
                  <MessageSquare className="w-4 h-4 mr-1" /> Chat Agent
                </Button>
              </a>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">Listing Aktif ({data.listings.length})</h2>
          {data.listings.length === 0 ? (
            <div className="rounded-xl bg-[#141C2E] border border-white/5 p-10 text-center text-slate-400">Belum ada listing aktif.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.listings.map((l) => (
                <Link key={l.id} to={`/p/listing/${l.id}`} data-testid={`public-listing-${l.id}`} className="group rounded-xl overflow-hidden border border-white/5 bg-[#141C2E] hover:border-orange-500/40 transition">
                  <div className="aspect-[4/3] bg-slate-800">
                    {l.photos?.[0] ? (
                      <img src={fileUrl(l.photos[0])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-slate-600"><Building2 className="w-10 h-10" /></div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-semibold line-clamp-1">{l.title}</div>
                    <div className="text-xs text-slate-400 line-clamp-1">{l.location}</div>
                    <div className="text-orange-400 font-bold mt-1">{formatRupiah(l.price)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
